import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

// Global Course (Simple Course Catalog - independent of classes)
export type Course = {
  _id: string
  title: string
  courseIdentifier: string
  createdAt?: string
  updatedAt?: string
}

export type CourseCreateData = {
  title: string
  courseIdentifier: string
  // Also accept legacy field names for backward compatibility
  name?: string
  code?: string
}

export type CourseUpdateData = {
  title?: string
  courseIdentifier?: string
  // Also accept legacy field names for backward compatibility
  name?: string
  code?: string
}

// Class Course (Course assigned to a class - kept for backward compatibility)
export type ClassCourse = {
  _id: string
  name: string
  courseIdentifier?: string
  class: {
    _id: string
    name: string
  }
  section: {
    _id: string
    name: string
  }
  session: {
    _id: string
    name: string
  }
  teacher: {
    _id: string
    firstName: string
    lastName:string
    email: string
  }
  branch: {
    _id: string
    name: string
  }
  syllabus: string[]
  status: "active" | "inactive"
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type CourseApiResponse = {
  success: boolean
  data: Course[]
}

export type SingleCourseApiResponse = {
  success: boolean
  data: Course
}

// Legacy response types for class courses
export type ClassCourseApiResponse = {
  data: ClassCourse[]
  pagination: PaginationData
  message: string
}

export type SingleClassCourseApiResponse = {
  success: boolean
  message: string
  data: ClassCourse
}

const COURSE_ENDPOINT = {
  COURSES: "/courses",
}

// Get all global courses (simple course catalog)
const getAllCourses = async ({
  searchText,
}: {
  searchText?: string
} = {}): Promise<CourseApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(COURSE_ENDPOINT.COURSES, {
      searchText,
    }),
    method: "GET",
  })
  return response.data
}

const getCourseById = async (id: string): Promise<SingleCourseApiResponse> => {
  const response = await apiClient({
    url: `${COURSE_ENDPOINT.COURSES}/${id}`,
    method: "GET",
  })
  return response.data
}

const createCourse = async (data: CourseCreateData): Promise<SingleCourseApiResponse> => {
  // Transform legacy field names to backend expected format
  const payload = {
    title: data.title || data.name || "",
    courseIdentifier: data.courseIdentifier || data.code || "",
  }
  
  const response = await apiClient({
    url: COURSE_ENDPOINT.COURSES,
    method: "POST",
    data: payload,
  })
  return response.data
}

const updateCourse = async (id: string, data: CourseUpdateData): Promise<SingleCourseApiResponse> => {
  // Transform legacy field names to backend expected format
  const payload: any = {}
  if (data.title || data.name) {
    payload.title = data.title || data.name
  }
  if (data.courseIdentifier || data.code) {
    payload.courseIdentifier = data.courseIdentifier || data.code
  }
  
  const response = await apiClient({
    url: `${COURSE_ENDPOINT.COURSES}/${id}`,
    method: "PUT",
    data: payload,
  })
  return response.data
}

const deleteCourse = async (id: string): Promise<{
  success: boolean
}> => {
  const response = await apiClient({
    url: `${COURSE_ENDPOINT.COURSES}/${id}`,
    method: "DELETE",
  })
  return response.data
}

export {
  getAllCourses,
  getCourseById,
  createCourse,
  updateCourse,
  deleteCourse,
}